import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sparkles, LogOut, Plus, Search, Bookmark, TrendingUp, Users, Target, CheckCircle2, X, Edit3, RefreshCw, Eye, ExternalLink, DollarSign, Zap, Star, Send, Info, Flame, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import Footer, { PlatformDisclaimer } from '@/components/Footer';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const BrandDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(location.state?.user || null);
  const [brandProfile, setBrandProfile] = useState(location.state?.brandProfile || null);
  const [campaigns, setCampaigns] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [creators, setCreators] = useState([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRecommendedModal, setShowRecommendedModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [applications, setApplications] = useState([]);
  const [recommendedCreators, setRecommendedCreators] = useState([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [niches, setNiches] = useState([]);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [expandedFactors, setExpandedFactors] = useState({});
  const [intelligenceData, setIntelligenceData] = useState(null);
  const [loadingIntelligence, setLoadingIntelligence] = useState(false);

  // Campaign form state
  const [campaignForm, setCampaignForm] = useState({
    campaign_title: '',
    campaign_description: '',
    niche_required: '',
    deliverables: '',
    estimated_budget_min: '',
    estimated_budget_max: ''
  });

  // Search filters
  const [filters, setFilters] = useState({
    niche: '',
    min_subscribers: '',
    min_score: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Skip if user passed from AuthCallback
    if (location.state?.user) {
      setLoading(false);
      loadDashboard();
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.role !== 'brand') {
        navigate('/dashboard'); // Redirect creators to creator dashboard
        return;
      }
      
      setUser(response.data);
      setBrandProfile(response.data.brand_profile);
      setLoading(false);
      loadDashboard();
    } catch (error) {
      console.error('Not authenticated:', error);
      navigate('/brands/login');
    }
  };

  const loadDashboard = async () => {
    await Promise.all([
      loadCampaigns(),
      loadBookmarks(),
      loadNiches()
    ]);
  };

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/brands/my-campaigns`, {
        withCredentials: true
      });
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const loadBookmarks = async () => {
    try {
      const response = await axios.get(`${API}/brands/bookmarks`, {
        withCredentials: true
      });
      setBookmarks(response.data.bookmarked_creators);
    } catch (error) {
      console.error('Failed to load bookmarks:', error);
    }
  };

  const loadNiches = async () => {
    try {
      const response = await axios.get(`${API}/brands/discover/niches`);
      setNiches(response.data.niches);
    } catch (error) {
      console.error('Failed to load niches:', error);
    }
  };

  const loadIntelligence = async () => {
    setLoadingIntelligence(true);
    try {
      const response = await axios.get(`${API}/brand-intelligence/dashboard`);
      setIntelligenceData(response.data);
    } catch (error) {
      console.error('Failed to load intelligence data:', error);
    } finally {
      setLoadingIntelligence(false);
    }
  };

  const searchCreators = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.niche) params.append('niche', filters.niche);
      if (filters.min_subscribers) params.append('min_subscribers', filters.min_subscribers);
      if (filters.min_score) params.append('min_score', filters.min_score);
      params.append('limit', '20');

      const response = await axios.get(`${API}/brands/discover/creators?${params}`);
      setCreators(response.data.creators);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search creators');
    }
  };

  const createCampaign = async () => {
    if (!campaignForm.campaign_title || !campaignForm.niche_required || !campaignForm.deliverables) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/campaigns/create`,
        campaignForm,
        { withCredentials: true }
      );
      toast.success('Campaign created successfully!');
      setShowCampaignModal(false);
      setCampaignForm({
        campaign_title: '',
        campaign_description: '',
        niche_required: '',
        deliverables: '',
        estimated_budget_min: '',
        estimated_budget_max: ''
      });
      loadCampaigns();
    } catch (error) {
      console.error('Failed to create campaign:', error);
      toast.error('Failed to create campaign');
    }
  };

  const viewApplications = async (campaign) => {
    setSelectedCampaign(campaign);
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.campaign_id}/applications`, {
        withCredentials: true
      });
      setApplications(response.data.applications);
      setShowApplicationsModal(true);
    } catch (error) {
      console.error('Failed to load applications:', error);
      toast.error('Failed to load applications');
    }
  };

  const updateApplicationStatus = async (applicationId, status) => {
    try {
      await axios.patch(
        `${API}/campaigns/applications/${applicationId}/status?status=${status}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Application ${status}`);
      // Reload applications
      viewApplications(selectedCampaign);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const closeCampaign = async (campaignId) => {
    try {
      await axios.patch(
        `${API}/campaigns/${campaignId}/status?status=closed`,
        {},
        { withCredentials: true }
      );
      toast.success('Campaign closed');
      loadCampaigns();
    } catch (error) {
      console.error('Failed to close campaign:', error);
      toast.error('Failed to close campaign');
    }
  };

  const reopenCampaign = async (campaignId) => {
    try {
      await axios.patch(
        `${API}/campaigns/${campaignId}/status?status=active`,
        {},
        { withCredentials: true }
      );
      toast.success('Campaign reopened');
      loadCampaigns();
    } catch (error) {
      console.error('Failed to reopen campaign:', error);
      toast.error('Failed to reopen campaign');
    }
  };

  const viewRecommendedCreators = async (campaign) => {
    setSelectedCampaign(campaign);
    setRecommendedLoading(true);
    setShowRecommendedModal(true);
    
    try {
      const response = await axios.get(`${API}/campaigns/${campaign.campaign_id}/recommended-creators`, {
        withCredentials: true
      });
      setRecommendedCreators(response.data.recommendations);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setRecommendedLoading(false);
    }
  };

  const inviteCreator = async (creatorId, creatorName) => {
    try {
      await axios.post(
        `${API}/campaigns/${selectedCampaign.campaign_id}/invite`,
        { creator_id: creatorId },
        { withCredentials: true }
      );
      toast.success(`Invitation sent to ${creatorName}!`);
      // Refresh recommendations to update invitation status
      viewRecommendedCreators(selectedCampaign);
    } catch (error) {
      console.error('Failed to invite creator:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const toggleFactorsExpand = (creatorId) => {
    setExpandedFactors(prev => ({...prev, [creatorId]: !prev[creatorId]}));
  };

  const openEditModal = (campaign) => {
    setEditingCampaign({
      ...campaign,
      campaign_title: campaign.campaign_title,
      campaign_description: campaign.campaign_description || '',
      niche_required: campaign.niche_required,
      deliverables: campaign.deliverables || '',
      estimated_budget_min: campaign.estimated_budget_min || '',
      estimated_budget_max: campaign.estimated_budget_max || ''
    });
    setShowEditModal(true);
  };

  const updateCampaign = async () => {
    if (!editingCampaign) return;

    try {
      const params = new URLSearchParams();
      if (editingCampaign.campaign_title) params.append('campaign_title', editingCampaign.campaign_title);
      if (editingCampaign.campaign_description) params.append('campaign_description', editingCampaign.campaign_description);
      if (editingCampaign.niche_required) params.append('niche_required', editingCampaign.niche_required);
      if (editingCampaign.deliverables) params.append('deliverables', editingCampaign.deliverables);
      if (editingCampaign.estimated_budget_min) params.append('estimated_budget_min', editingCampaign.estimated_budget_min);
      if (editingCampaign.estimated_budget_max) params.append('estimated_budget_max', editingCampaign.estimated_budget_max);

      await axios.put(
        `${API}/campaigns/${editingCampaign.campaign_id}?${params}`,
        {},
        { withCredentials: true }
      );
      toast.success('Campaign updated successfully!');
      setShowEditModal(false);
      setEditingCampaign(null);
      loadCampaigns();
    } catch (error) {
      console.error('Failed to update campaign:', error);
      toast.error('Failed to update campaign');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-slate-600">{brandProfile?.brand_name || user?.name}</span>
            <Button onClick={handleLogout} variant="ghost" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}} data-testid="brand-dashboard-heading">
            Brand Dashboard
          </h1>
          <p className="text-lg text-slate-600">Manage campaigns, discover creators, and track applications</p>
        </div>

        <Tabs defaultValue="campaigns" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="campaigns">My Campaigns</TabsTrigger>
            <TabsTrigger value="discover">Discover Creators</TabsTrigger>
            <TabsTrigger value="intelligence" onClick={loadIntelligence}>Intelligence</TabsTrigger>
            <TabsTrigger value="bookmarks">Saved Creators</TabsTrigger>
          </TabsList>

          {/* My Campaigns Tab */}
          <TabsContent value="campaigns">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-slate-600">{campaigns.length} campaigns created</p>
              <Button onClick={() => setShowCampaignModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-campaign-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </div>

            {campaigns.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-slate-600 mb-4">You haven't created any campaigns yet</p>
                <Button onClick={() => setShowCampaignModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Create Your First Campaign
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {campaigns.map((campaign, index) => (
                  <Card key={campaign.campaign_id} className="p-6" data-testid={`campaign-card-${index}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>{campaign.campaign_title}</h3>
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                          {campaign.status === 'active' ? 'Open' : 'Closed'}
                        </span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditModal(campaign)}
                        data-testid={`edit-campaign-${index}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{campaign.campaign_description}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Niche</p>
                        <p className="text-sm font-medium text-slate-700">{campaign.niche_required}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Applications</p>
                        <p className="text-2xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>{campaign.application_count || 0}</p>
                      </div>
                    </div>
                    
                    {(campaign.estimated_budget_min || campaign.estimated_budget_max) && (
                      <div className="mb-4 flex items-center gap-2 text-sm text-slate-600">
                        <DollarSign className="w-4 h-4" />
                        <span>
                          ${campaign.estimated_budget_min?.toLocaleString() || '0'} - ${campaign.estimated_budget_max?.toLocaleString() || '∞'}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button onClick={() => viewApplications(campaign)} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`view-applications-${index}`}>
                        <Eye className="w-4 h-4 mr-2" />
                        Applications
                      </Button>
                      <Button 
                        onClick={() => viewRecommendedCreators(campaign)} 
                        variant="outline" 
                        className="flex-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        data-testid={`view-recommended-${index}`}
                      >
                        <Zap className="w-4 h-4 mr-2" />
                        Find Creators
                      </Button>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {campaign.status === 'active' ? (
                        <Button onClick={() => closeCampaign(campaign.campaign_id)} variant="outline" size="sm" className="flex-1" data-testid={`close-campaign-${index}`}>
                          Close Campaign
                        </Button>
                      ) : (
                        <Button onClick={() => reopenCampaign(campaign.campaign_id)} variant="outline" size="sm" className="flex-1 text-green-600 border-green-200 hover:bg-green-50" data-testid={`reopen-campaign-${index}`}>
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Reopen
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Discover Creators Tab */}
          <TabsContent value="discover">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Filters */}
              <div className="lg:col-span-1">
                <Card className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Filters</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Niche</Label>
                      <select 
                        className="w-full h-10 px-3 border border-slate-200 rounded-md"
                        value={filters.niche}
                        onChange={(e) => setFilters({...filters, niche: e.target.value})}
                      >
                        <option value="">All Niches</option>
                        {niches.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="mb-2 block">Min. Subscribers</Label>
                      <Input
                        type="number"
                        value={filters.min_subscribers}
                        onChange={(e) => setFilters({...filters, min_subscribers: e.target.value})}
                        placeholder="e.g., 10000"
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block">Min. Score</Label>
                      <Input
                        type="number"
                        value={filters.min_score}
                        onChange={(e) => setFilters({...filters, min_score: e.target.value})}
                        placeholder="e.g., 70"
                      />
                    </div>
                    <Button onClick={searchCreators} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Results */}
              <div className="lg:col-span-3">
                {creators.length === 0 ? (
                  <Card className="p-12 text-center">
                    <p className="text-slate-600">Search creators using the filters</p>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {creators.map((creator, index) => (
                      <Card key={creator.creator_id} className="p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">{creator.channel_name}</h3>
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">Subscribers</span>
                            <span className="font-semibold">{creator.subscriber_count?.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">Score</span>
                            <span className="font-semibold text-indigo-600">{creator.sponsorship_readiness_score}%</span>
                          </div>
                        </div>
                        <Link to={`/creator/${creator.username || creator.creator_id}`}>
                          <Button className="w-full">View Profile</Button>
                        </Link>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Intelligence Dashboard Tab */}
          <TabsContent value="intelligence">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
                  <BarChart3 className="w-6 h-6 inline mr-2 text-indigo-600" />
                  Sponsorship Intelligence Dashboard
                </h2>
                <p className="text-slate-600">Insights from creator analysis and brand detection across the platform</p>
              </div>

              {loadingIntelligence ? (
                <div className="py-16 text-center">
                  <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-slate-600">Loading intelligence data...</p>
                </div>
              ) : intelligenceData ? (
                <div className="grid gap-6">
                  {/* Platform Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="p-4 text-center">
                      <p className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                        {intelligenceData.platform_stats?.total_creators_analyzed || 0}
                      </p>
                      <p className="text-sm text-slate-600">Creators Analyzed</p>
                    </Card>
                    <Card className="p-4 text-center">
                      <p className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                        {intelligenceData.platform_stats?.total_brands_detected || 0}
                      </p>
                      <p className="text-sm text-slate-600">Brands Detected</p>
                    </Card>
                    <Card className="p-4 text-center">
                      <p className="text-3xl font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                        {intelligenceData.platform_stats?.total_campaigns || 0}
                      </p>
                      <p className="text-sm text-slate-600">Total Campaigns</p>
                    </Card>
                    <Card className="p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-600" style={{fontFamily: 'JetBrains Mono'}}>
                        {intelligenceData.platform_stats?.active_campaigns || 0}
                      </p>
                      <p className="text-sm text-slate-600">Active Campaigns</p>
                    </Card>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Top Sponsoring Brands */}
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                        Top Sponsoring Brands
                      </h3>
                      {intelligenceData.top_sponsoring_brands?.length > 0 ? (
                        <div className="space-y-3">
                          {intelligenceData.top_sponsoring_brands.slice(0, 8).map((brand, idx) => (
                            <div key={brand.brand_name} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-600">
                                  {idx + 1}
                                </span>
                                <span className="font-medium text-slate-700">{brand.brand_name}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-sm font-semibold text-indigo-600">{brand.mention_count} mentions</span>
                                <p className="text-xs text-slate-500">{brand.niche_category}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No brand data yet</p>
                      )}
                    </Card>

                    {/* Niche Sponsorship Activity */}
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                        Niche Sponsorship Activity
                      </h3>
                      {intelligenceData.niche_sponsorship_activity?.length > 0 ? (
                        <div className="space-y-3">
                          {intelligenceData.niche_sponsorship_activity.slice(0, 8).map((niche) => (
                            <div key={niche.niche} className="flex items-center justify-between">
                              <span className="text-slate-700">{niche.niche}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-indigo-500 rounded-full"
                                    style={{width: `${Math.min(niche.creator_count * 10, 100)}%`}}
                                  ></div>
                                </div>
                                <span className="text-sm font-semibold text-slate-600 w-8 text-right">{niche.creator_count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-center py-4">No niche data yet</p>
                      )}
                    </Card>
                  </div>

                  {/* Deal Value Tiers */}
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                      Average Deal Value by Subscriber Tier
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {intelligenceData.deal_value_by_tier?.map((tier) => (
                        <div key={tier.tier} className="p-4 bg-slate-50 rounded-lg text-center">
                          <p className="text-sm font-medium text-slate-500 mb-1">{tier.tier}</p>
                          <p className="text-lg font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>{tier.avg_deal}</p>
                          <p className="text-xs text-slate-500">{tier.creator_count} creators</p>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Trending Categories */}
                  {intelligenceData.trending_categories?.length > 0 && (
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                        Trending Sponsorship Categories
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {intelligenceData.trending_categories.map((cat) => (
                          <span 
                            key={cat.category}
                            className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium"
                          >
                            {cat.category} ({cat.total_mentions} mentions)
                          </span>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* High Readiness Creators */}
                  {intelligenceData.creators_likely_to_accept?.length > 0 && (
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                        Creators Most Likely to Accept Deals
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {intelligenceData.creators_likely_to_accept.slice(0, 6).map((creator) => (
                          <div key={creator.creator_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900">{creator.channel_name}</p>
                              <p className="text-sm text-slate-600">{creator.primary_niche || creator.niche}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-emerald-600" style={{fontFamily: 'JetBrains Mono'}}>
                                {creator.sponsorship_readiness_score || creator.sponsorship_probability}%
                              </p>
                              <p className="text-xs text-slate-500">Readiness</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              ) : (
                <Card className="p-12 text-center">
                  <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Click the Intelligence tab to load sponsorship insights</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Bookmarks Tab */}
          <TabsContent value="bookmarks">
            {bookmarks.length === 0 ? (
              <Card className="p-12 text-center">
                <Bookmark className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No saved creators yet</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {bookmarks.map((creator, index) => (
                  <Card key={creator.bookmark_id} className="p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{creator.channel_name}</h3>
                    <p className="text-sm text-slate-600 mb-4">{creator.niche}</p>
                    <Link to={`/creator/${creator.username || creator.creator_id}`}>
                      <Button className="w-full">View Profile</Button>
                    </Link>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Campaign Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Campaign</DialogTitle>
            <DialogDescription>Fill in the details for your sponsorship campaign</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Campaign Title *</Label>
              <Input
                value={campaignForm.campaign_title}
                onChange={(e) => setCampaignForm({...campaignForm, campaign_title: e.target.value})}
                placeholder="e.g., Tech Product Review Campaign"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={campaignForm.campaign_description}
                onChange={(e) => setCampaignForm({...campaignForm, campaign_description: e.target.value})}
                placeholder="Describe your campaign goals and requirements"
              />
            </div>
            <div>
              <Label>Niche *</Label>
              <select 
                className="w-full h-10 px-3 border border-slate-200 rounded-md"
                value={campaignForm.niche_required}
                onChange={(e) => setCampaignForm({...campaignForm, niche_required: e.target.value})}
              >
                <option value="">Select niche</option>
                {niches.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <Label>Deliverables *</Label>
              <Textarea
                value={campaignForm.deliverables}
                onChange={(e) => setCampaignForm({...campaignForm, deliverables: e.target.value})}
                placeholder="e.g., 1 YouTube video, 3 Instagram posts"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Budget ($)</Label>
                <Input
                  type="number"
                  value={campaignForm.estimated_budget_min}
                  onChange={(e) => setCampaignForm({...campaignForm, estimated_budget_min: e.target.value})}
                />
              </div>
              <div>
                <Label>Max Budget ($)</Label>
                <Input
                  type="number"
                  value={campaignForm.estimated_budget_max}
                  onChange={(e) => setCampaignForm({...campaignForm, estimated_budget_max: e.target.value})}
                />
              </div>
            </div>
            <Button onClick={createCampaign} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
              Create Campaign
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Applications Modal */}
      <Dialog open={showApplicationsModal} onOpenChange={setShowApplicationsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedCampaign?.campaign_title} - Applications</DialogTitle>
            <DialogDescription>{applications.length} applications received</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {applications.map((app, index) => {
              const demoCreatorNames = ['Tech Explained', 'Fitness Journey', 'Beauty Basics'];
              const isDemo = demoCreatorNames.includes(app.creator_details?.channel_name) || app.creator_details?.is_demo === true;
              
              return (
              <Card key={app.application_id} className={`p-6 ${isDemo ? 'border-amber-200 bg-amber-50/30' : ''}`} data-testid={`application-card-${index}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-lg">{app.creator_details?.channel_name}</h4>
                      {isDemo ? (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full" title="This is a demo creator">
                          Example
                        </span>
                      ) : app.creator_details?.youtube_link && (
                        <a 
                          href={app.creator_details.youtube_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {app.creator_details?.subscriber_count?.toLocaleString()} subscribers
                      </span>
                      <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">
                        {app.creator_details?.niche}
                      </span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded ${
                    app.status === 'accepted' ? 'bg-green-100 text-green-700' :
                    app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                  </span>
                </div>
                
                {/* Creator Stats */}
                <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Engagement Rate</p>
                    <p className="text-lg font-semibold text-slate-900" style={{fontFamily: 'JetBrains Mono'}}>
                      {app.creator_details?.engagement_rate || 'N/A'}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Sponsorship Score</p>
                    <p className="text-lg font-semibold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                      {app.creator_details?.sponsorship_readiness_score || 'N/A'}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Match Likelihood</p>
                    <p className="text-lg font-semibold text-green-600" style={{fontFamily: 'JetBrains Mono'}}>
                      {Math.round((app.creator_details?.sponsorship_readiness_score || 50) * 0.9)}%
                    </p>
                  </div>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Creator's Pitch:</p>
                  <p className="text-sm text-slate-600 bg-white border border-slate-200 rounded p-3">{app.pitch}</p>
                </div>
                {app.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => updateApplicationStatus(app.application_id, 'accepted')}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`accept-application-${index}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Accept
                    </Button>
                    <Button
                      onClick={() => updateApplicationStatus(app.application_id, 'rejected')}
                      variant="outline"
                      className="flex-1"
                      data-testid={`reject-application-${index}`}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}
              </Card>
              );
            })}
            {applications.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No applications received yet
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Campaign Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Campaign</DialogTitle>
            <DialogDescription>Update your campaign details</DialogDescription>
          </DialogHeader>
          {editingCampaign && (
            <div className="space-y-4">
              <div>
                <Label>Campaign Title *</Label>
                <Input
                  value={editingCampaign.campaign_title}
                  onChange={(e) => setEditingCampaign({...editingCampaign, campaign_title: e.target.value})}
                  placeholder="e.g., Tech Product Review Campaign"
                  data-testid="edit-campaign-title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingCampaign.campaign_description}
                  onChange={(e) => setEditingCampaign({...editingCampaign, campaign_description: e.target.value})}
                  placeholder="Describe your campaign goals and requirements"
                  data-testid="edit-campaign-description"
                />
              </div>
              <div>
                <Label>Niche *</Label>
                <select 
                  className="w-full h-10 px-3 border border-slate-200 rounded-md"
                  value={editingCampaign.niche_required}
                  onChange={(e) => setEditingCampaign({...editingCampaign, niche_required: e.target.value})}
                  data-testid="edit-campaign-niche"
                >
                  <option value="">Select niche</option>
                  {niches.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <Label>Deliverables</Label>
                <Textarea
                  value={editingCampaign.deliverables}
                  onChange={(e) => setEditingCampaign({...editingCampaign, deliverables: e.target.value})}
                  placeholder="e.g., 1 YouTube video, 3 Instagram posts"
                  data-testid="edit-campaign-deliverables"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Budget ($)</Label>
                  <Input
                    type="number"
                    value={editingCampaign.estimated_budget_min}
                    onChange={(e) => setEditingCampaign({...editingCampaign, estimated_budget_min: e.target.value})}
                    data-testid="edit-campaign-min-budget"
                  />
                </div>
                <div>
                  <Label>Max Budget ($)</Label>
                  <Input
                    type="number"
                    value={editingCampaign.estimated_budget_max}
                    onChange={(e) => setEditingCampaign({...editingCampaign, estimated_budget_max: e.target.value})}
                    data-testid="edit-campaign-max-budget"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={updateCampaign} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-campaign-btn">
                  Save Changes
                </Button>
                <Button onClick={() => setShowEditModal(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recommended Creators Modal */}
      <Dialog open={showRecommendedModal} onOpenChange={setShowRecommendedModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-emerald-600" />
              Recommended Creators
            </DialogTitle>
            <DialogDescription>
              AI-powered creator recommendations for "{selectedCampaign?.campaign_title}"
            </DialogDescription>
          </DialogHeader>
          
          {recommendedLoading ? (
            <div className="py-12 text-center">
              <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Finding the best creators for your campaign...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recommendedCreators.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No matching creators found for this campaign niche
                </div>
              ) : (
                recommendedCreators.map((creator, index) => (
                  <Card key={creator.creator_id} className="p-5" data-testid={`recommended-creator-${index}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                            {creator.channel_name}
                          </h3>
                          {creator.demand_signals?.is_trending && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                              <Flame className="w-3 h-3" />
                              Trending
                            </span>
                          )}
                          {creator.invitation_status === 'pending' && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                              Invited
                            </span>
                          )}
                          {creator.invitation_status === 'accepted' && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              Accepted
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-slate-500 uppercase mb-1">Platform</p>
                            <p className="text-sm font-medium text-slate-700">{creator.platform}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 uppercase mb-1">Subscribers</p>
                            <p className="text-sm font-medium text-slate-700">{creator.subscriber_count?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 uppercase mb-1">Niche</p>
                            <p className="text-sm font-medium text-slate-700">{creator.niche}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 uppercase mb-1">Sponsorship Likelihood</p>
                            <p className="text-sm font-semibold text-indigo-600">{creator.sponsorship_likelihood}%</p>
                          </div>
                        </div>

                        {/* Campaign Match Score */}
                        <div className="bg-emerald-50 rounded-lg p-3 mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">Campaign Match Score</span>
                            <span className="text-2xl font-bold text-emerald-600" style={{fontFamily: 'JetBrains Mono'}}>
                              {creator.campaign_match_score}%
                            </span>
                          </div>
                          <div className="h-2 bg-emerald-100 rounded-full overflow-hidden mb-2">
                            <div 
                              className="h-full bg-emerald-500 rounded-full transition-all"
                              style={{width: `${creator.campaign_match_score}%`}}
                            ></div>
                          </div>
                          
                          {/* Explainable Factors */}
                          <button 
                            onClick={() => toggleFactorsExpand(creator.creator_id)}
                            className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800"
                          >
                            <Info className="w-3 h-3" />
                            {expandedFactors[creator.creator_id] ? 'Hide factors' : 'Show match factors'}
                          </button>
                          
                          {expandedFactors[creator.creator_id] && creator.match_factors && (
                            <div className="mt-3 space-y-2 text-xs">
                              {Object.entries(creator.match_factors).map(([key, factor]) => (
                                <div key={key} className="flex items-center justify-between">
                                  <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                                  <div className="flex items-center gap-2">
                                    <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                      <div 
                                        className="h-full bg-emerald-500 rounded-full"
                                        style={{width: `${(factor.score / factor.max) * 100}%`}}
                                      ></div>
                                    </div>
                                    <span className="text-slate-700 font-medium w-12 text-right">{factor.score}/{factor.max}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Demand Signals */}
                        {creator.demand_signals?.is_trending && (
                          <p className="text-xs text-orange-600 mb-3">
                            <Flame className="w-3 h-3 inline mr-1" />
                            {creator.demand_signals.trending_reason}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 mt-2">
                      {!creator.invitation_status ? (
                        <Button 
                          onClick={() => inviteCreator(creator.creator_id, creator.channel_name)}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`invite-creator-${index}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Invite Creator
                        </Button>
                      ) : (
                        <Button 
                          disabled
                          className="flex-1"
                          variant="outline"
                        >
                          {creator.invitation_status === 'pending' ? 'Invitation Sent' : 
                           creator.invitation_status === 'accepted' ? 'Accepted' : 'Declined'}
                        </Button>
                      )}
                      <Link to={`/creator/${creator.username || creator.creator_id}`} className="flex-1">
                        <Button variant="outline" className="w-full" data-testid={`view-profile-${index}`}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Profile
                        </Button>
                      </Link>
                    </div>
                  </Card>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Platform Disclaimer */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <PlatformDisclaimer />
      </div>
      
      {/* Footer */}
      <Footer showDisclaimer={false} />
    </div>
  );
};

export default BrandDashboard;
